/**
 * Structured logging utility
 * Provides environment-aware logging with consistent formatting
 */

import { ENV } from '../config/app.config';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

/**
 * Determine if logs at given level should be output based on environment
 */
const shouldLog = (level: LogLevel): boolean => {
  if (ENV === 'production') {
    // Production: only errors
    return level === 'error';
  }
  if (ENV === 'staging') {
    // Staging: errors, warnings, and info (no debug)
    return ['error', 'warn', 'info'].includes(level);
  }
  // Development: all levels
  return true;
};

/**
 * Format log message with timestamp and context
 */
const formatMessage = (
  level: LogLevel,
  message: string,
  context?: LogContext
): string => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

  if (context && Object.keys(context).length > 0) {
    return `${prefix} ${message} | Context: ${JSON.stringify(context)}`;
  }

  return `${prefix} ${message}`;
};

/**
 * Logger class with structured logging methods
 */
class Logger {
  /**
   * Debug-level logging (development only)
   */
  debug(message: string, context?: LogContext): void {
    if (shouldLog('debug')) {
      console.debug(formatMessage('debug', message, context));
    }
  }

  /**
   * Info-level logging (development + staging)
   */
  info(message: string, context?: LogContext): void {
    if (shouldLog('info')) {
      console.info(formatMessage('info', message, context));
    }
  }

  /**
   * Warning-level logging (all environments except production)
   */
  warn(message: string, context?: LogContext): void {
    if (shouldLog('warn')) {
      console.warn(formatMessage('warn', message, context));
    }
  }

  /**
   * Error-level logging (all environments)
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (shouldLog('error')) {
      const errorContext = {
        ...context,
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
        } : error,
      };
      console.error(formatMessage('error', message, errorContext));
    }
  }

  /**
   * Log socket connection events (staging + dev only)
   */
  socket(event: string, data?: any): void {
    if (ENV !== 'production') {
      this.info(`Socket: ${event}`, data ? { data } : undefined);
    }
  }

  /**
   * Log API requests (staging + dev only)
   */
  api(method: string, url: string, status?: number, error?: any): void {
    if (ENV !== 'production') {
      if (error) {
        this.warn(`API ${method} ${url} failed`, { status, error });
      } else {
        this.debug(`API ${method} ${url}`, { status });
      }
    }
  }
}

export const logger = new Logger();
